import React, { useEffect, useMemo, useState } from "react";
import { Grid, Box, Stack, MenuItem, Card, Divider } from "@mui/material";
import { H4 } from "components/Typography";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useTranslation } from "react-i18next";
import RouteAuth from "components/positeams/routeAuth";
import ReportPerQuiz from "page-sections/reports/perQuiz";
import ReportPerGroup from "page-sections/reports/perGroup";
import ReportPerCurriculum from "page-sections/reports/perCurriculum";
import ReportPerInstructor from "page-sections/reports/perInstructor";
import useAuth from "hooks/useAuth";
import {
  HRBP_ROLE_ID,
  MANAGER_ROLE_ID,
  QUIZ_TYPE_COLD,
  QUIZ_TYPE_HOT,
} from "../../constants";
import AppTextField from "components/input-fields/AppTextField";
import useHasRightPermission from "hooks/useHasRightPermission";
import TrainingQuizPerParticipant from "page-sections/reports/trainingQuizPerParticipant";
import TrainingQuizPerModule from "page-sections/reports/trainingQuizPerModule";

const Reports = () => {
  return (
    <RouteAuth>
      <Content />
    </RouteAuth>
  );
};
const Content = () => {
  const { t } = useTranslation();
  const { role } = useAuth();

  const [quizType, setQuizType] = useState(QUIZ_TYPE_COLD);

  const { hasPermission: hasColdQuizPermission } =
    useHasRightPermission("cold-quiz-reports");
  const { hasPermission: hasHotQuizPermission } =
    useHasRightPermission("hot-quiz-reports");
  const { hasPermission: trainingQuizPermission } =
    useHasRightPermission("training-reports");

  const quizTypes = useMemo(() => {
    let _quizTypes = [
      { id: QUIZ_TYPE_HOT, name: t("General.hot_quiz") },
      { id: QUIZ_TYPE_COLD, name: t("General.cold_quiz") },
    ];

    if (!hasColdQuizPermission)
      _quizTypes = _quizTypes.filter((e) => e.id !== QUIZ_TYPE_COLD);
    if (!hasHotQuizPermission)
      _quizTypes = _quizTypes.filter((e) => e.id !== QUIZ_TYPE_HOT);

    return _quizTypes;
  }, [hasColdQuizPermission, hasHotQuizPermission]);

  // initialize the quiz type
  useEffect(() => {
    if (quizTypes?.length) setQuizType(quizTypes[0].id);
  }, [quizTypes]);

  const handleQuizTypeChange = (event) => {
    setQuizType(event.target.value);
  };

  return (
    <Box pt={2} pb={4}>
      <Grid container spacing={3}>
        <Grid item md={10} xs={12}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <AssessmentIcon />
              <H4>{t("Reports.title")}</H4>
              &nbsp;
            </Stack>

            <AppTextField
              select
              value={quizType}
              onChange={handleQuizTypeChange}
              name="curriculum"
              disabled={false}
              sx={{ width: 250 }}
              label={t("Reports.quiz_type")}
            >
              <div></div>
              {quizTypes?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </AppTextField>
          </Stack>

          <Card sx={{ padding: 3, mt: 3 }}>
            <ReportPerQuiz quizType={quizType} />
            <Divider sx={{ my: 3 }} />

            {role?.id != MANAGER_ROLE_ID && role?.id != HRBP_ROLE_ID && (
              <>
                <ReportPerGroup quizType={quizType} />
                <Divider sx={{ my: 3 }} />

                <ReportPerCurriculum quizType={quizType} />
                <Divider sx={{ my: 3 }} />
              </>
            )}
            <ReportPerInstructor quizType={quizType} />
          </Card>

          {trainingQuizPermission && (
            <Card sx={{ padding: 3, mt: 3 }}>
              <TrainingQuizPerParticipant quizType={quizType} />
              <Divider sx={{ my: 3 }} />
              <TrainingQuizPerModule quizType={quizType} />
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports;
